// Raid-damage state + repair flow.
//
// While a raid is active (idleManager flips raidActive=true), wild goons can
// torch BUILDINGS and FARM plots. The set of damaged tiles is kept on the
// active kingdom slice (`damagedTiles`). When the raid ends and at least
// one damage exists, the UI prompts the player with two choices:
//
//   1) PAID  — pay Mao Coin proportional to damage, instant repair
//   2) LABOR — dedicate citizens for a timed repair. While `repair.mode`
//              is 'labor', goonManager applies `laborGoonRateMult` (0)
//              so the player earns nothing until repairs finish.
//
// Damage state survives reloads (it lives on the kingdom save). Burning
// FARM plots paint to `scorchedTileColor` via the PlotDamaged event; the
// scene/buildings module hooks BuildingDamaged to attach a fire mesh.

import { Events, emit, on } from '../ui/eventBus.js'
import { RaidDamageConfig } from '../config/balance.js'
import { active, persist } from './state.js'
import * as season from './seasonManager.js'

let tickHandle = null

export function init() {
  // Per-second tick handles labor-repair countdown.
  if (tickHandle) clearInterval(tickHandle)
  tickHandle = setInterval(tickRepair, 1000)
}

// Idempotent flagger called by wildGoons when a goon torches something.
export function damageTile(tileIndex, kind /* 'building' | 'plot' */) {
  const k = active()
  if (!k.damagedTiles) k.damagedTiles = {}
  const key = String(tileIndex)
  if (k.damagedTiles[key]) return false
  k.damagedTiles[key] = { kind, at: Date.now() }
  persist()
  if (kind === 'building') emit(Events.BuildingDamaged, { tileIndex })
  else emit(Events.PlotDamaged, { tileIndex })
  return true
}

export function isDamaged(tileIndex) {
  return !!active().damagedTiles?.[String(tileIndex)]
}

export function damageCount() {
  return Object.keys(active().damagedTiles ?? {}).length
}

export function isRepairing() {
  return active().repair?.mode === 'labor'
}

export function getRepair() {
  return active().repair ?? null
}

// Paid repair — drains coin proportional to damage and clears every
// damaged tile in a single beat. Returns false if the player can't afford.
export function startPaidRepair() {
  const k = active()
  const n = damageCount()
  if (n === 0) return false
  const cost = n * RaidDamageConfig.paidRepairCostPerDamage
  if (!season.spendWealth(cost)) return false
  finishRepair('paid', n)
  return true
}

// Labor repair — dedicates citizens for a timed window. While active,
// `goonManager` reads `laborGoonRateMult` (0) so all coin production halts
// until the labor ends.
export function startLaborRepair() {
  const k = active()
  const n = damageCount()
  if (n === 0) return false
  const duration = n * RaidDamageConfig.laborRepairMsPerDamage
  k.repair = {
    mode: 'labor',
    endsAt: Date.now() + duration,
    totalDamages: n,
  }
  persist()
  emit(Events.RepairStart, { mode: 'labor', endsAt: k.repair.endsAt, count: n })
  return true
}

function finishRepair(mode, count) {
  const k = active()
  const cleared = Object.keys(k.damagedTiles ?? {})
  k.damagedTiles = {}
  k.repair = null
  persist()
  // Fire per-tile cleared events so scene listeners can remove fire meshes
  // and re-paint scorched FARM plots back to their normal seasonal color.
  for (const idx of cleared) emit(Events.PlotChanged, { tileIndex: Number(idx) })
  emit(Events.RepairComplete, { mode, count })
}

// Per-second poll for labor-repair completion.
function tickRepair() {
  const r = active().repair
  if (!r || r.mode !== 'labor') return
  if (Date.now() >= r.endsAt) {
    finishRepair('labor', r.totalDamages)
  }
}
